"use client";

/**
 * Last-resort error boundary.
 *
 * This catches what nothing else can: an error thrown by the root layout
 * itself, or a render that fails before any page-level boundary exists. React
 * has already torn the tree down by the time this runs, so it replaces the
 * root layout — no shared shell, no fonts, no Tailwind. Hence inline styles:
 * anything that depends on the app's own CSS pipeline could be the very thing
 * that just broke.
 *
 * Reporting matters more than the design here. Without this, a root-layout
 * failure is a white screen the customer walks away from and we never hear
 * about.
 */
import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";

export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#ffffff",
          color: "#000000",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif",
        }}
      >
        <main style={{ maxWidth: "34rem", padding: "2rem", textAlign: "center" }}>
          <p
            style={{
              margin: 0,
              fontSize: "0.7rem",
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#fc5500",
            }}
          >
            Area Advertising
          </p>
          <h1 style={{ margin: "0.75rem 0 0", fontSize: "2rem", lineHeight: 1.1 }}>
            Something went wrong on our end.
          </h1>
          <p style={{ margin: "1rem 0 2rem", color: "#4b5563", lineHeight: 1.5 }}>
            This page failed to load. It has been reported to us automatically. Reload to
            try again, or call the shop at{" "}
            <a href="tel:+16063298055" style={{ color: "#000000" }}>
              (606) 329-8055
            </a>{" "}
            and we will take care of it.
          </p>
          {/* A plain <a>, not next/link, on purpose: the router is part of
              what just failed here, so a client-side navigation would try to
              recover inside the broken tree. This forces a full document
              load. */}
          {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
          <a
            href="/"
            style={{
              display: "inline-block",
              padding: "0.9rem 1.75rem",
              background: "#000000",
              color: "#ffffff",
              textDecoration: "none",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.85rem",
            }}
          >
            Back to the homepage
          </a>
          {error.digest && (
            <p style={{ marginTop: "2rem", fontSize: "0.75rem", color: "#9ca3af" }}>
              Reference: {error.digest}
            </p>
          )}
        </main>
      </body>
    </html>
  );
}
